// Use the shared authenticated Supabase client
import { supabase } from './supabase'

export interface Activity {
  id: string
  user_id: string
  timestamp: string
  action_type: string
  entity_type?: string
  entity_id?: string
  details?: Record<string, any>
}

export type ActionType =
  // Page actions
  | 'page_created'
  | 'page_updated'
  | 'page_deleted'
  | 'page_viewed'
  | 'page_merged'
  // Task actions
  | 'task_created'
  | 'task_updated'
  | 'task_completed'
  | 'task_uncompleted'
  | 'task_deleted'
  // Note actions
  | 'note_created'
  | 'note_updated'
  | 'note_deleted'
  // Comment actions
  | 'comment_created'
  | 'comment_updated'
  | 'comment_deleted'
  // Chat actions
  | 'chat_message_sent'
  | 'chat_message_received'
  | 'chat_conversation_started'
  // Search & filter actions
  | 'search_performed'
  | 'filter_applied'
  | 'sort_changed'
  | 'group_changed'
  // User actions
  | 'user_signed_in'
  | 'user_signed_out'
  | 'settings_updated'
  | 'data_migrated'
  // Import actions
  | 'markdown_imported'
  | 'data_imported'
  // System actions
  | 'sync_completed'
  | 'error_occurred'

export interface LogActivityParams {
  userId: string
  actionType: ActionType
  entityType?: string
  entityId?: string
  details?: Record<string, any>
}

function isAbortError(error: any): boolean {
  const message = (error?.message || '').toString()
  const details = (error?.details || '').toString()
  return message.includes('AbortError') || details.includes('AbortError')
}

/**
 * ActivityLogger - Centralized logging service
 * Logs all user actions with timestamps to Supabase
 */
export class ActivityLogger {
  private static instance: ActivityLogger | null = null

  private constructor() {}

  static getInstance(): ActivityLogger {
    if (!ActivityLogger.instance) {
      ActivityLogger.instance = new ActivityLogger()
    }
    return ActivityLogger.instance
  }

  /**
   * Log an activity
   */
  async log(params: LogActivityParams): Promise<void> {
    try {
      const { userId, actionType, entityType, entityId, details } = params

      const activity = {
        user_id: userId,
        timestamp: new Date().toISOString(),
        action_type: actionType,
        entity_type: entityType,
        entity_id: entityId,
        details: details || {},
      }

      const { error } = await supabase.from('activities').insert(activity)

      if (error) {
        if (!isAbortError(error)) {
          console.error('[ActivityLogger] Error logging activity:', error)
        }
      } else {
        console.log(`[ActivityLogger] Logged: ${actionType}`, details)
      }
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('[ActivityLogger] Exception:', error)
      }
    }
  }

  /**
   * Get activities for a user with optional filters
   * Uses cursor-based pagination for better performance
   */
  async getActivities(params: {
    userId: string
    actionType?: string
    entityType?: string
    search?: string
    limit?: number
    cursor?: { timestamp: string; id: string } // Cursor for pagination
  }): Promise<Activity[]> {
    try {
      let query = supabase
        .from('activities')
        .select('*')
        .eq('user_id', params.userId)

      // Apply cursor (only fetch rows older than cursor)
      if (params.cursor) {
        // Use composite cursor: timestamp < X OR (timestamp = X AND id < Y)
        query = query.or(`timestamp.lt.${params.cursor.timestamp},and(timestamp.eq.${params.cursor.timestamp},id.lt.${params.cursor.id})`)
      }

      if (params.actionType) {
        query = query.eq('action_type', params.actionType)
      }

      if (params.entityType) {
        query = query.eq('entity_type', params.entityType)
      }

      if (params.search) {
        // SECURITY: Escape search term to prevent SQL injection
        const { escapeLikePattern } = await import('./security-utils')
        const escapedSearch = escapeLikePattern(params.search)
        // Search in details JSONB field
        query = query.or(`details->>'name'.ilike.%${escapedSearch}%,details->>'content'.ilike.%${escapedSearch}%`)
      }

      query = query
        .order('timestamp', { ascending: false })
        .order('id', { ascending: false }) // Secondary sort for stable cursor

      if (params.limit) {
        query = query.limit(params.limit)
      }

      const { data, error } = await query

      if (error) {
        console.error('[ActivityLogger] Error fetching activities:', error)
        return []
      }

      return (data as Activity[]) || []
    } catch (error) {
      console.error('[ActivityLogger] Exception:', error)
      return []
    }
  }

  /**
   * Get activity count for a user
   */
  async getActivityCount(userId: string, actionType?: string): Promise<number> {
    try {
      let query = supabase
        .from('activities')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)

      if (actionType) {
        query = query.eq('action_type', actionType)
      }

      const { count, error } = await query

      if (error) {
        console.error('[ActivityLogger] Error counting activities:', error)
        return 0
      }

      return count || 0
    } catch (error) {
      console.error('[ActivityLogger] Exception:', error)
      return 0
    }
  }

  /**
   * Clear all activities for a user
   */
  async clearActivities(userId: string): Promise<void> {
    try {
      const { error } = await supabase.from('activities').delete().eq('user_id', userId)

      if (error) {
        console.error('[ActivityLogger] Error clearing activities:', error)
      } else {
        console.log('[ActivityLogger] Cleared all activities')
      }
    } catch (error) {
      console.error('[ActivityLogger] Exception:', error)
    }
  }

  /**
   * Export activities to JSON
   */
  async exportActivities(userId: string): Promise<Activity[]> {
    return this.getActivities({ userId, limit: 10000 })
  }
}

// Export singleton instance
export const activityLogger = ActivityLogger.getInstance()
